import { useCallback, useEffect, useRef, useState } from "react";
import type { OutputData } from "@smartsuite/automation-engine-api";
import { useEngineClient } from "../../../../services/engineClient";

type OutputsFetcher = (id: string) => Promise<OutputData[] | undefined>;

/**
 * Lazy per-component cache over an outputs-returning engine RPC: fetches when
 * `enabled` flips true (expand) and the id is a real engine id (`""` marks a
 * pre-7.25 job -> caller's legacy path), then serves the cached result for the
 * component's lifetime. `undefined` means the fetch hasn't resolved yet
 * (callers may show a loading skeleton); a failed fetch or absent payload
 * settles the cache to `[]` so render paths degrade to "no box" (never a
 * stuck skeleton, never a throw, no retry storm) -- same degradation contract
 * as useRecordJobData.
 *
 * An in-flight id is never re-requested (collapse -> re-expand while the
 * first response is pending reuses it), and a response always settles the
 * cache -- the data is immutable per id, so discarding it would only force a
 * duplicate RPC later. Only unmount stops the state write.
 */
const useLazyOutputs = (
  fetcher: OutputsFetcher,
  id: string,
  enabled: boolean,
): OutputData[] | undefined => {
  const [cache, setCache] = useState<Record<string, OutputData[]>>({});
  const inFlight = useRef<Set<string>>(new Set());
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  useEffect(() => {
    if (
      !enabled ||
      id === "" ||
      cache[id] !== undefined ||
      inFlight.current.has(id)
    ) {
      return;
    }
    inFlight.current.add(id);
    void fetcher(id)
      .catch(() => undefined)
      .then((outputs) => {
        inFlight.current.delete(id);
        if (mounted.current) {
          setCache((prev) => ({ ...prev, [id]: outputs ?? [] }));
        }
      });
  }, [enabled, id, cache, fetcher]);

  return cache[id];
};

export const useLoopItemOutputs = (
  jobId: string,
  loopItemId: string,
  enabled: boolean,
): OutputData[] | undefined => {
  const engineClient = useEngineClient();
  const fetcher = useCallback(
    async (id: string) =>
      (await engineClient.getLoopItemData({ jobId, loopItemId: id })).loopItem
        ?.outputs,
    [engineClient, jobId],
  );
  return useLazyOutputs(fetcher, loopItemId, enabled);
};

export const useBranchOutputs = (
  jobId: string,
  branchId: string,
  enabled: boolean,
): OutputData[] | undefined => {
  const engineClient = useEngineClient();
  const fetcher = useCallback(
    async (id: string) =>
      (await engineClient.getBranchData({ jobId, branchId: id })).branchData
        ?.outputs,
    [engineClient, jobId],
  );
  return useLazyOutputs(fetcher, branchId, enabled);
};
